import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api } from "./api";

export interface EventTypeBreakdownItem {
  eventType: string;
  count: number;
}

export type IdentityEngine = "postgres" | "dual" | "bigquery";

export interface ProjectDetail {
  id: number;
  name: string;
  /** TASK-228 — ISO-Z, null when not archived. */
  archivedAt: string | null;
  funnelId: number;
  funnelMark: string;
  bqDataset: string;
  apiSecretMasked: string;
  allowedDomains: string[];
  active: boolean;
  customHostname: string | null;
  subdomain: string | null;
  mergeReviewEnabled: boolean;
  identityEngine: IdentityEngine;
  bqSyncEnabled: boolean;
  bqSyncLastAt: string | null;
  webhookSigningRequired: boolean;
  engagementTrackingEnabled: boolean;
  trackerSnippet: string | null;
  iframeTrackerSnippet: string | null;
  createdAt: string;
  updatedAt: string;
  workspace: "agentur" | "marketingabteilung";
  externalId: string | null;
  stats: {
    last24h: number;
    sparkline: number[];
    days: string[];
    eventTypeBreakdown: EventTypeBreakdownItem[];
    available: boolean;
  };
  fairUseRejectionsToday: number;
}

const detailKey = (id: number) => ["admin", "projects", "detail", id];
const pipelineKey = (id: number) => ["admin", "projects", "pipeline", id];

export interface ProjectPipelineRecentEvent {
  eventId: string;
  eventType: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  dealValue: number | null;
  dealCurrency: string | null;
  source: string | null;
  finishedAt: string | null;
}

export interface ProjectPipeline {
  queue: {
    waiting: number;
    active: number;
    delayed: number;
    failed: number;
  };
  pendingApproval: number;
  processedToday: number;
  processed24h: number;
  recentEvents: ProjectPipelineRecentEvent[];
}

export function useProjectPipeline(id: number) {
  return useQuery<ProjectPipeline>({
    queryKey: pipelineKey(id),
    queryFn: () => api<ProjectPipeline>(`/admin/projects/${id}/pipeline`),
    refetchInterval: 5_000,
    staleTime: 0,
  });
}

export function useProjectDetail(id: number) {
  return useQuery<ProjectDetail>({
    queryKey: detailKey(id),
    queryFn: () => api<ProjectDetail>(`/admin/projects/${id}`),
    staleTime: 30_000,
  });
}

export function useUpdateProject(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name?: string; allowedDomains?: string[] }) =>
      api<{ ok: true }>(`/admin/projects/${id}`, {
        method: "PATCH",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: detailKey(id) });
      qc.invalidateQueries({ queryKey: ["admin", "projects", "list"] });
    },
  });
}

export interface ProjectCustomerLink {
  configured: boolean;
  kundeRecId: string | null;
}

const customerLinkKey = (id: number) =>
  ["admin", "projects", "customer-link", id] as const;

/** Aktuell verknüpfter DHC-Kunde dieses Projekts (dh_portal.PortalConfig). */
export function useProjectCustomerLink(id: number) {
  return useQuery<ProjectCustomerLink>({
    queryKey: customerLinkKey(id),
    queryFn: () =>
      api<ProjectCustomerLink>(`/admin/projects/${id}/customer-link`),
    staleTime: 30_000,
  });
}

/** Kunden-Verknüpfung setzen/wechseln/lösen (kundeRecId=null = kein Kunde). */
export function useSetProjectCustomerLink(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (kundeRecId: string | null) =>
      api<{ ok: boolean; kundeRecId: string | null }>(
        `/admin/projects/${id}/customer-link`,
        { method: "POST", body: JSON.stringify({ kundeRecId }) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: customerLinkKey(id) });
    },
  });
}

export function useRotateSecret(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ ok: true; secret: string }>(`/admin/projects/${id}/rotate-secret`, {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: detailKey(id) }),
  });
}

export function useToggleActive(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (nextActive: boolean) =>
      api<{ ok: true }>(
        `/admin/projects/${id}/${nextActive ? "activate" : "deactivate"}`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: detailKey(id) });
      qc.invalidateQueries({ queryKey: ["admin", "projects", "list"] });
    },
  });
}

// TASK-228 — archive / restore. Both refresh detail and list (the list hides archived).
export function useArchiveProject(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (archive: boolean) =>
      api<{ ok: true; hostnameRemoved?: boolean }>(
        `/admin/projects/${id}/${archive ? "archive" : "unarchive"}`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: detailKey(id) });
      qc.invalidateQueries({ queryKey: ["admin", "projects", "list"] });
    },
  });
}

export function useToggleMergeReview(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      api<{ ok: true; mergeReviewEnabled: boolean }>(
        `/admin/projects/${id}/merge-review`,
        {
          method: "POST",
          body: JSON.stringify({ enabled }),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: detailKey(id) });
    },
  });
}

/**
 * Sentinel phrase the backend requires when disabling bq_sync_enabled.
 * Must match the constant in apps/api/src/routes/admin.ts.
 */
export const DISABLE_BQ_SYNC_CONFIRM_PHRASE =
  "I-understand-this-stops-bigquery-mirror";

interface EngineUpdate {
  identityEngine?: IdentityEngine;
  bqSyncEnabled?: boolean;
  confirmDisableBqSync?: string;
}

interface EngineResponse {
  ok: true;
  identityEngine: IdentityEngine;
  bqSyncEnabled: boolean;
  bqSyncLastAt: string | null;
}

export function useUpdateEngine(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EngineUpdate) =>
      api<EngineResponse>(`/admin/projects/${id}/engine`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: detailKey(id) });
      qc.invalidateQueries({ queryKey: ["admin", "projects", "list"] });
    },
  });
}

export function useRevealWebhookSecret(id: number) {
  return useMutation({
    mutationFn: () =>
      api<{ ok: true; secret: string }>(
        `/admin/projects/${id}/webhook-secret`,
      ),
  });
}

export function useRotateWebhookSecret(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ ok: true; secret: string }>(
        `/admin/projects/${id}/rotate-webhook-secret`,
        { method: "POST" },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: detailKey(id) }),
  });
}

export function useToggleEngagementTracking(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      api<{ ok: true; engagementTrackingEnabled: boolean }>(
        `/admin/projects/${id}/engagement-tracking`,
        {
          method: "POST",
          body: JSON.stringify({ enabled }),
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: detailKey(id) }),
  });
}

export function useToggleWebhookSigning(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (required: boolean) =>
      api<{ ok: true; webhookSigningRequired: boolean }>(
        `/admin/projects/${id}/webhook-signing`,
        {
          method: "POST",
          body: JSON.stringify({ required }),
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: detailKey(id) }),
  });
}
